import type { ReactNode } from 'react';
import type { TableOfContents } from '@models/Toc';
import type { AccordionHeading } from '@mdx-cmp/Accordion/context/AccordionHeader';

const extractTextFromReactNode = (node: ReactNode): string => {
  if (typeof node === 'string') {
    return node;
  }

  if (typeof node === 'number') {
    return String(node);
  }

  if (node instanceof Object && 'children' in node) {
    return extractTextFromReactNode(node.children);
  }

  if (node instanceof Object && 'props' in node) {
    return extractTextFromReactNode(node.props?.children);
  }

  if (node instanceof Object && Symbol.iterator in node) {
    return Array.from(node as Iterable<ReactNode>)
      .map((childNode: ReactNode) => extractTextFromReactNode(childNode))
      .join('');
  }

  return '';
};

const findAnchor = (tableOfContents: TableOfContents, headingTitle: string, id: string): string => {
  let anchor = '';

  const traverseContents = (tableOfContents: TableOfContents, headingTitle: string, id: string): void => {
    for (const content of tableOfContents) {
      if (content.title === headingTitle && id === content.link.replace('#', '')) {
        anchor = content?.link;
        return;
      }

      if (content?.items?.length) {
        traverseContents(content.items, headingTitle, id);
      }
    }
  };

  traverseContents(tableOfContents, headingTitle, id);

  return anchor;
};

const createAnchorLink = (anchor: string) =>
  `${globalThis?.location?.origin}${globalThis?.location?.pathname}${anchor}`;

export const useAnchorLink = (
  tableOfContents: TableOfContents,
  headingTitle: ReactNode,
  id: string,
  accordionHeading: AccordionHeading | void
) => {
  if (accordionHeading) {
    return createAnchorLink(
      findAnchor(tableOfContents, extractTextFromReactNode(accordionHeading.headingTitle), accordionHeading.id)
    );
  }
  return createAnchorLink(findAnchor(tableOfContents, extractTextFromReactNode(headingTitle), id));
};
